import { startOfDay } from 'date-fns'
import { useMemo } from 'react'

export function useMonthView(month: number, year: number, value: Date): Array<
	Array<{
		date: number
		isCurrentMonth: boolean
		isSelected: boolean
		isToday: boolean
		isWeekday: boolean
		isWeekend: boolean
		jsDate: Date
	}>
> {
	return useMemo(() => {
		const daysInMonth = new Date(year, month + 1, 0).getDate()
		const firstDayOfMonth = (new Date(year, month, 1).getDay() + 6) % 7
		const today = new Date()
		const isCurrentYear = today.getFullYear() === year
		const isCurrentMonth = today.getMonth() === month

		type DayEntry = {
			date: number
			isCurrentMonth: boolean
			isSelected: boolean
			isToday: boolean
			isWeekday: boolean
			isWeekend: boolean
			jsDate: Date
		}

		// create array of days for the current month
		const days: DayEntry[] = []
		for (let i = 1; i <= daysInMonth; i++) {
			const date = new Date(year, month, i)
			const dayOfWeek = (date.getDay() + 6) % 7
			days.push({
				date: i,
				isCurrentMonth: true,
				isSelected: value.getDate() === i && value.getMonth() === month && value.getFullYear() === year,
				isToday: isCurrentYear && isCurrentMonth && today.getDate() === i,
				isWeekday: dayOfWeek < 5,
				isWeekend: dayOfWeek >= 5,
				jsDate: startOfDay(date),
			})
		}

		// Add padding days from previous month
		const prevMonthDays: DayEntry[] = []
		const prevMonthLastDay = new Date(year, month, 0).getDate()
		for (let i = firstDayOfMonth - 1; i >= 0; i--) {
			const date = new Date(year, month - 1, prevMonthLastDay - i)
			const dayOfWeek = (date.getDay() + 6) % 7
			prevMonthDays.push({
				date: prevMonthLastDay - i,
				isCurrentMonth: false,
				isSelected: false,
				isToday: false,
				isWeekday: dayOfWeek < 5,
				isWeekend: dayOfWeek >= 5,
				jsDate: startOfDay(date),
			})
		}

		// Add padding days from next month
		const nextMonthDays: DayEntry[] = []
		const totalDays = prevMonthDays.length + days.length
		const remainingDays = 42 - totalDays // 6 rows * 7 days = 42
		for (let i = 1; i <= remainingDays; i++) {
			const date = new Date(year, month + 1, i)
			const dayOfWeek = (date.getDay() + 6) % 7
			nextMonthDays.push({
				date: i,
				isCurrentMonth: false,
				isSelected: false,
				isToday: false,
				isWeekday: dayOfWeek < 5,
				isWeekend: dayOfWeek >= 5,
				jsDate: startOfDay(date),
			})
		}

		// Combine all days and split into weeks
		const allDays = [...prevMonthDays, ...days, ...nextMonthDays]
		const weeks: DayEntry[][] = []
		for (let i = 0; i < allDays.length; i += 7) {
			weeks.push(allDays.slice(i, i + 7))
		}

		return weeks
	}, [month, year, value])
}
